import React, { useState, useEffect, useRef } from "react";
import { X, Zap, LucideIcon, CircleCheck } from "lucide-react";
import ReconnectingWebSocket from "reconnecting-websocket";
import { marked } from "marked";
import EmojiPicker, { EmojiClickData } from "emoji-picker-react";
import purify from "dompurify";
import ChatInterface from "./chatInterface";
import aposTropheIcon from "@/assets/invertedcommas.svg";
import { useLocation } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

const generateSessionId = () => {
    return Math.random().toString(36).substr(2, 9);
};

interface ChatMessage {
    text: string;
    sender: string;
    parsedObj?: any;
}

interface ChatWindowProps {
    onClose: () => void;
    selectedchatAgent: string;
    name: string;
    chatHints: any;
    chatIcon: any;
    agentRole?: string;
    agentName: string;
    features: string[];
    activities: string[];
    description?: string;
    language?: string;
    agentId: string;
}

interface SidebarSectionProps {
    title: string;
    children: React.ReactNode;
    className?: string;
}

interface HintItemProps {
    text: string;
}

interface FeatureItemProps {
    text: string;
    enabled?: boolean;
}

interface ActivityItemProps {
    text: string;
    icon: LucideIcon;
    status?: "active" | "completed" | "warning";
}

const SidebarSection: React.FC<SidebarSectionProps> = ({
    title,
    children,
    className = ""
}) => (
    <div className={`${className}`}>
        <h2 className="text-2xl font-normal text-gray-500 mb-8 text-left font-inter">
            {title}
        </h2>
        {children}
    </div>
);

const HintItem: React.FC<HintItemProps> = ({ text }) => (
    <div className="flex items-center space-x-3">
        <div className="w-2 h-2 bg-white rounded-full"></div>
        <p className="text-gray-300 text-base md:text-xl font-manrope">{text}</p>
    </div>
);

const FeatureItem: React.FC<FeatureItemProps> = ({ text, enabled = true }) => (
  <div className="flex items-start space-x-3">
    <span
      className="flex items-start justify-center"
      style={{ width: 15, height: 15, minWidth: 15, minHeight: 15 }}
    >
      <CircleCheck
        size={20}
        className={enabled ? "text-green-400" : "text-gray-500"}
      />
    </span>
    <p
      className={`text-base md:text-xl font-manrope ${enabled ? "text-gray-300" : "text-gray-500"}`}
    >
      {text}
    </p>
  </div>
);

const ActivityItem: React.FC<ActivityItemProps> = ({
    text,
    icon: Icon,
    status = "active"
}) => (
    <div className="flex items-start space-x-3">
        <span
            className="flex items-start justify-center"
            style={{ width: 15, height: 15, minWidth: 15, minHeight: 15 }}
        >
            <Icon size={15} className="text-gray-400" />
        </span>
        <p className="text-base md:text-xl font-manrope text-gray-300">{text}</p>
    </div>
);

function ChatWindow({
    onClose,
    selectedchatAgent,
    name,
    chatHints,
    chatIcon,
    agentRole,
    agentName,
    features,
    activities,
    description,
    language,
    agentId
}: ChatWindowProps) {
    const chatServerUrl: string = process.env.REACT_APP_CHAT_SERVER_URL || "";
    const chatApiKey: string = process.env.REACT_APP_CHAT_API_KEY || "";
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [input, setInput] = useState("");
    const [sessionId] = useState(generateSessionId);
    const wsRef = useRef<ReconnectingWebSocket | null>(null);
    const [typing, setTyping] = useState(false);
    const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const routerLocation = useLocation();

    // Determine initial active tab based on available content
    const getInitialTab = (): 'hints' | 'features' | 'activities' => {
        if (chatHints && chatHints.length > 0) return 'hints';
        if (features && features.length > 0) return 'features';
        if (activities && activities.length > 0) return 'activities';
        return 'hints';
    };
    const [activeTab, setActiveTab] = useState<'hints' | 'features' | 'activities'>(getInitialTab);

    // Check if any sidebar content exists
    const hasSidebarContent =
        (chatHints && chatHints.length > 0) ||
        (features && features.length > 0) ||
        (activities && activities.length > 0);

    useEffect(() => {
        const widget = document.getElementById('nurix-widget');
        if (widget) widget.classList.toggle('hidden', true);

        return () => {
            if (widget) widget.classList.toggle('hidden', false);
        }
    }, [routerLocation.pathname]);

    function extractJsonObjectFromString(input: any) {
        const marker = "PRODUCT_DATA_JSON";
        const markerIdx = input.indexOf(marker);
        if (markerIdx !== -1) {
            const codeBlockStart = input.indexOf("```json", markerIdx);
            if (codeBlockStart !== -1) {
                const jsonStart = input.indexOf("{", codeBlockStart);
                const codeBlockEnd = input.indexOf("```", jsonStart);
                if (jsonStart !== -1 && codeBlockEnd !== -1) {
                    const jsonString = input.slice(jsonStart, codeBlockEnd);
                    let json = null;
                    try {
                        json = JSON.parse(jsonString);
                    } catch (e) { }
                    const text = (
                        input.slice(0, markerIdx) + input.slice(codeBlockEnd + 3)
                    )
                        .replace(/\s+/g, " ")
                        .trim();
                    return { json, text };
                }
            }
        }

        const parsedDataIdx = input.indexOf("parsedData");
        if (parsedDataIdx !== -1) {
            const braceIdx = input.indexOf("{", parsedDataIdx);
            const bracketIdx = input.indexOf("[", parsedDataIdx);
            if (braceIdx !== -1) {
                const jsonEnd = input.indexOf("}", braceIdx);
                if (jsonEnd !== -1) {
                    const jsonString = input.slice(braceIdx, jsonEnd + 1);
                    let json = null;
                    try {
                        json = JSON.parse(jsonString);
                    } catch (e) { }
                    const text = input
                        .slice(0, parsedDataIdx)
                        .replace(/\s+/g, " ")
                        .trim();
                    return { json, text };
                }
            }
            if (bracketIdx !== -1) {
                const jsonEnd = input.indexOf("]", bracketIdx);
                if (jsonEnd !== -1) {
                    const jsonString = input.slice(bracketIdx, jsonEnd + 1);
                    let json = null;
                    try {
                        json = JSON.parse(jsonString);
                    } catch (e) { }
                    const text = input
                        .slice(0, parsedDataIdx)
                        .replace(/\s+/g, " ")
                        .trim();
                    return { json, text };
                }
            }
        }

        return { json: null, text: input };
    }

    useEffect(() => {
        const userId = uuidv4()

        const wsUrl = `${chatServerUrl}/v2/chat/INTERNAL_CHAT/nuplay/${sessionId}?user_id=${userId}&api_key=${chatApiKey}&agent_id=${agentId}`;

        wsRef.current = new ReconnectingWebSocket(wsUrl);

        wsRef.current.onopen = () => {
            setMessages([]);
        };

        wsRef.current.onmessage = (event) => {
            const response = JSON.parse(event.data);
            if (response.response_type === "typing") {
                setTyping(true);
            }
            if (response.response_type === "response") {
                if (typingTimeoutRef.current) {
                    clearTimeout(typingTimeoutRef.current);
                    typingTimeoutRef.current = null;
                }
                let { json: parsedObj, text: updatedContent } =
                    extractJsonObjectFromString(response.content);
                const botMessage = { text: updatedContent, sender: "bot", parsedObj };

                setMessages((prevMessages) => [...prevMessages, botMessage]);
                setTyping(false);
            }
        };

        return () => {
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }
            wsRef.current?.close();
        };
    }, [sessionId, selectedchatAgent]);

    const sendMessage = async (message: string) => {
        if (message.trim() === "") return;

        const userMessage = { text: message, sender: "user" };
        setMessages((prevMessages) => [...prevMessages, userMessage]);
        setInput("");
        setTyping(true);

        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
        }
        typingTimeoutRef.current = setTimeout(() => {
            setTyping(false);
        }, 30000);

        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            const requestPayload = {
                text: message.trim(),
            };

            wsRef.current.send(
                JSON.stringify(requestPayload)
            );
        }
    };

    return (
        <>
            <div className="hidden lg:flex h-screen w-screen bg-black text-white font-manrope relative">
                {/* Close Button - Positioned at top left */}
                <div className="absolute top-10 left-8 z-20">
                    <button
                        className="w-14 h-14 bg-gray-700 rounded-full flex items-center justify-center hover:bg-gray-600 transition-colors"
                        onClick={onClose}
                    >
                        <X size={30} className="text-gray-300" />
                    </button>
                </div>

                {/* Centered Heading - Across Full Page Width */}
                <div className="absolute top-10 left-0 right-0 flex justify-center z-10">
                    <h1 className="text-4xl font-semibold text-gray-300 font-inter tracking-wide whitespace-nowrap px-4 py-2 rounded-lg backdrop-blur-sm">
                        {agentRole} Agent
                    </h1>
                </div>

                {hasSidebarContent ? (
                    // Layout with sidebar (2/5 - 3/5)
                    <>
                        {/* Left Sidebar - 2/5 width */}
                        <div className="w-2/5 flex flex-col items-start justify-center pl-28">
                            <div className="flex flex-col space-y-16 w-full max-w-sm">
                                {chatHints && chatHints.length > 0 && (
                                    <SidebarSection title="Chat Hints">
                                        <div className="space-y-4">
                                            {chatHints.map((hintItem: any, index: number) => {
                                                const hintText = typeof hintItem === 'string' ? hintItem : hintItem.hint || hintItem.triggers;
                                                return <HintItem key={index} text={hintText} />;
                                            })}
                                        </div>
                                    </SidebarSection>
                                )}

                                {activities && activities.length > 0 && (
                                    <SidebarSection title="Activities">
                                        <div className="space-y-4">
                                            {activities.map((activity, index) => (
                                                <ActivityItem
                                                    key={index}
                                                    text={activity}
                                                    icon={Zap}
                                                    status="active"
                                                />
                                            ))}
                                        </div>
                                    </SidebarSection>
                                )}

                                {features && features.length > 0 && (
                                    <SidebarSection title="Features">
                                        <div className="space-y-4">
                                            {features.map((feature, index) => (
                                                <FeatureItem key={index} text={feature} />
                                            ))}
                                        </div>
                                    </SidebarSection>
                                )}
                            </div>
                        </div>

                        {/* Right Chat Interface - 3/5 width */}
                        <div className="w-3/5 flex flex-col">
                            <div className="flex-1 flex flex-col items-center justify-center p-8 pt-24">
                                <div className="w-full h-[80vh]">
                                    <ChatInterface
                                        agentName={agentName}
                                        icon={chatIcon}
                                        onEndChat={onClose}
                                        messages={messages}
                                        onSendMessage={sendMessage}
                                        input={input}
                                        setInput={setInput}
                                        typing={typing}
                                    />
                                </div>
                            </div>
                        </div>
                    </>
                ) : (
                    // Centered layout when no sidebar content
                    <div className="w-full flex flex-col items-center justify-center">
                        <div className="w-full max-w-4xl flex flex-col items-center justify-center p-8 pt-24">
                            <div className="w-full h-[80vh]">
                                <ChatInterface
                                    agentName={agentName}
                                    icon={chatIcon}
                                    onEndChat={onClose}
                                    messages={messages}
                                    onSendMessage={sendMessage}
                                    input={input}
                                    setInput={setInput}
                                    typing={typing}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Mobile Version - Hidden on desktop */}
            <div className="flex lg:hidden fixed inset-0 bg-black text-white flex-col font-manrope mobile-full-height">
                {/* Header */}
                <div className="flex-shrink-0 flex items-center p-4 pb-2">
                    <button
                        className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center hover:bg-gray-600 transition-colors"
                        onClick={onClose}
                    >
                        <X size={20} className="text-gray-300" />
                    </button>

                    <h1 className="text-lg font-normal text-gray-300 font-inter ml-4">
                        Chat with {agentName}
                    </h1>
                </div>

                {/* Main content area */}
                <div className="flex-1 flex flex-col min-h-0">
                    {/* Chat Interface */}
                    <div className={`flex justify-center px-4 py-2 min-h-0 ${hasSidebarContent ? 'flex-[7]' : 'flex-1'}`}>
                        <div className="w-full h-full">
                            <ChatInterface
                                agentName={agentName}
                                icon={chatIcon}
                                onEndChat={onClose}
                                messages={messages}
                                onSendMessage={sendMessage}
                                input={input}
                                setInput={setInput}
                                typing={typing}
                            />
                        </div>
                    </div>

                    {/* Mobile Info Sections - with tabbed interface */}
                    {hasSidebarContent && (
                        <div className="flex-[3] flex flex-col p-4 pt-2 border-t border-gray-800 min-h-0">
                            {/* Tab buttons */}
                            <div className="flex space-x-2 mb-3 overflow-x-auto flex-shrink-0">
                                {chatHints && chatHints.length > 0 && (
                                    <button
                                        onClick={() => setActiveTab('hints')}
                                        className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                                            activeTab === 'hints'
                                                ? 'bg-gray-700 text-white'
                                                : 'bg-gray-800 text-gray-400 hover:bg-gray-700/50'
                                        }`}
                                    >
                                        Chat Hints
                                    </button>
                                )}
                                {features && features.length > 0 && (
                                    <button
                                        onClick={() => setActiveTab('features')}
                                        className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                                            activeTab === 'features'
                                                ? 'bg-gray-700 text-white'
                                                : 'bg-gray-800 text-gray-400 hover:bg-gray-700/50'
                                        }`}
                                    >
                                        Features
                                    </button>
                                )}
                                {activities && activities.length > 0 && (
                                    <button
                                        onClick={() => setActiveTab('activities')}
                                        className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                                            activeTab === 'activities'
                                                ? 'bg-gray-700 text-white'
                                                : 'bg-gray-800 text-gray-400 hover:bg-gray-700/50'
                                        }`}
                                    >
                                        Activities
                                    </button>
                                )}
                            </div>

                            {/* Tab content - scrollable */}
                            <div className="flex-1 overflow-y-auto space-y-2 touch-scroll">
                                {activeTab === 'hints' && chatHints && chatHints.length > 0 && (
                                    chatHints.map((hintItem: any, index: number) => {
                                        const hintText = typeof hintItem === 'string' ? hintItem : hintItem.hint || hintItem.triggers;
                                        return (
                                            <div key={index} className="flex items-center space-x-2">
                                                <div className="w-1.5 h-1.5 bg-white rounded-full flex-shrink-0"></div>
                                                <p className="text-sm text-gray-400">{hintText}</p>
                                            </div>
                                        );
                                    })
                                )}

                                {activeTab === 'features' && features && features.length > 0 && (
                                    features.map((feature, index) => (
                                        <div key={index} className="flex items-center space-x-2">
                                            <CircleCheck size={14} className="text-green-400 flex-shrink-0" />
                                            <p className="text-sm text-gray-400">{feature}</p>
                                        </div>
                                    ))
                                )}

                                {activeTab === 'activities' && activities && activities.length > 0 && (
                                    activities.map((activity, index) => (
                                        <div key={index} className="flex items-center space-x-2">
                                            <Zap size={14} className="text-gray-400 flex-shrink-0" />
                                            <p className="text-sm text-gray-400">{activity}</p>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}

export default ChatWindow;
